import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MoneyTextInput } from '@/components/ui/money-input';
import { FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Layers, Plus, RefreshCw, Trash2, WifiOff } from 'lucide-react';
import { maskBRLInput } from '@/lib/currencyInput';
import { formatStockQuantity } from '@/lib/stock';
import { INTERNAL_CODE_MAX_LENGTH } from '@/lib/productCodes';
import { applyVariantTemplate, VARIANT_TEMPLATES } from '@/lib/variantTemplates';
import type { VariantDraft } from '@/lib/variants';
import { toast } from 'sonner';

/**
 * "Variações" — o bloco que monta P/M/G de um produto.
 *
 * POR QUE É UM COMPONENTE: ele nasceu inline no formulário do catálogo e passou
 * a ser preciso também no card de item do orçamento (o "salvar este item no meu
 * catálogo" cadastra um produto de verdade, e um produto de verdade pode ter
 * variações). Reescrevê-lo na segunda tela criaria duas implementações da mesma
 * regra — a semente da primeira variação, a validação e o texto de "exige
 * conexão" divergiriam na primeira correção de um dos lados.
 *
 * O componente NÃO grava: ele edita um rascunho. Quem persiste é o `handleSave`
 * de cada tela, junto do resto do item — as filhas precisam do id real do pai,
 * que só existe depois do insert.
 */
export interface VariantDraftsSectionProps {
  value: VariantDraft[];
  onChange: (drafts: VariantDraft[]) => void;
  /** Variações são online-only (#30c): sem rede o bloco fica inerte e avisado. */
  isOnline: boolean;
  /** `false` desenha o selo PRO ao lado do título. */
  allowed: boolean;
  /**
   * Portão de plano. Devolve `false` quando o paywall foi aberto — nesse caso
   * nada é acrescentado.
   */
  onRequirePro: () => boolean;
  /** Gera um código interno livre a partir de um nome (ou `null` se não deu). */
  generateCode: (source: string) => string | null;
  /**
   * O que a PRIMEIRA variação herda do produto simples que está virando grupo.
   * Sem isso, converter um produto em variações jogaria fora o preço e o custo
   * que o usuário acabou de digitar.
   */
  seed: { price: string; cost: string; barcode: string; internalCode: string };
  /** Nome do produto — entra na sugestão de código da variação (#30d). */
  parentName: string;
  /** Mostra o saldo de cada variação já salva. */
  showStock?: boolean;
  disabled?: boolean;
}

const newKey = (suffix: string | number = '') =>
  `variant_${Date.now()}_${suffix}_${Math.random().toString(36).slice(2, 8)}`;

export function VariantDraftsSection({
  value,
  onChange,
  isOnline,
  allowed,
  onRequirePro,
  generateCode,
  seed,
  parentName,
  showStock = false,
  disabled = false,
}: VariantDraftsSectionProps) {
  const addVariantDraft = () => {
    if (!onRequirePro()) return;
    const isFirst = value.length === 0;
    onChange([
      ...value,
      {
        key: newKey(value.length),
        variant_name: '',
        price: isFirst ? seed.price : '',
        cost: isFirst ? seed.cost : '',
        barcode: isFirst ? seed.barcode : '',
        internal_code: isFirst ? seed.internalCode : '',
      },
    ]);
  };

  const handleApplyVariantTemplate = (templateId: string) => {
    if (!onRequirePro()) return;
    if (!isOnline) {
      toast.warning('Gerenciar variações exige conexão com a internet.');
      return;
    }
    const template = VARIANT_TEMPLATES.find((item) => item.id === templateId);
    if (!template) return;

    onChange(
      applyVariantTemplate(value, template, (variantName, index) => {
        const isFirst = index === 0;
        return {
          key: newKey(index),
          variant_name: variantName,
          price: isFirst ? seed.price : '',
          cost: isFirst ? seed.cost : '',
          barcode: isFirst ? seed.barcode : '',
          internal_code: isFirst ? seed.internalCode : '',
        };
      }),
    );
  };

  const updateVariantDraft = (key: string, patch: Partial<VariantDraft>) => {
    onChange(value.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  };

  const removeVariantDraft = (key: string) => {
    onChange(value.filter((d) => d.key !== key));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="flex items-center gap-1.5 text-sm font-medium">
              Variações
              {!allowed && (
                <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold leading-none text-white shadow">
                  PRO
                </span>
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              Tamanhos, cores ou embalagens com preço e código de barras próprios.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <FieldLabel>Modelo pronto (opcional)</FieldLabel>
        <Select onValueChange={handleApplyVariantTemplate} disabled={!isOnline || disabled}>
          <SelectTrigger>
            <SelectValue placeholder="Escolha um modelo para preencher nomes" />
          </SelectTrigger>
          <SelectContent>
            {VARIANT_TEMPLATES.map((template) => (
              <SelectItem key={template.id} value={template.id}>
                {template.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Apenas acrescenta nomes ausentes. Você pode editar ou remover tudo depois.
        </p>
      </div>

      {value.length > 0 && (
        <div className="space-y-3">
          {value.map((draft, index) => (
            <div key={draft.key} className="space-y-2 rounded-md border bg-muted/30 p-2.5">
              <div className="flex items-center gap-2">
                <Input
                  aria-label={`Nome da variação ${index + 1}`}
                  placeholder="Ex: P"
                  value={draft.variant_name}
                  onChange={(e) => updateVariantDraft(draft.key, { variant_name: e.target.value })}
                  maxLength={80}
                  disabled={disabled}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-destructive"
                  onClick={() => removeVariantDraft(draft.key)}
                  aria-label={`Remover variação ${draft.variant_name || index + 1}`}
                  disabled={disabled}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  aria-label={`Preço da variação ${index + 1}`}
                  placeholder="Preço (0,00)"
                  inputMode="numeric"
                  value={draft.price}
                  onChange={(e) =>
                    updateVariantDraft(draft.key, { price: maskBRLInput(e.target.value) })
                  }
                  disabled={disabled}
                />
                <MoneyTextInput
                  aria-label={`Custo da variação ${index + 1}`}
                  placeholder="Custo (0,00)"
                  value={draft.cost}
                  onChange={(masked) => updateVariantDraft(draft.key, { cost: masked })}
                  disabled={disabled}
                />
                <Input
                  aria-label={`Código de barras da variação ${index + 1}`}
                  inputMode="numeric"
                  placeholder="Código de barras"
                  value={draft.barcode}
                  onChange={(e) => updateVariantDraft(draft.key, { barcode: e.target.value })}
                  className="col-span-2 font-mono"
                  disabled={disabled}
                />
                <div className="col-span-2 flex items-center gap-2">
                  <Input
                    aria-label={`Código interno da variação ${index + 1}`}
                    placeholder="Código interno"
                    value={draft.internal_code}
                    onChange={(e) => updateVariantDraft(draft.key, { internal_code: e.target.value })}
                    maxLength={INTERNAL_CODE_MAX_LENGTH}
                    className="font-mono"
                    disabled={disabled}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0"
                    onClick={() => {
                      // Nome do PAI + nome da variação: é o que distingue
                      // `CAM-P` de `CAM-M` na etiqueta. Só o nome do pai
                      // daria o mesmo código para as três.
                      const code = generateCode(`${parentName} ${draft.variant_name}`);
                      if (code) updateVariantDraft(draft.key, { internal_code: code });
                    }}
                    aria-label={`Gerar código interno da variação ${index + 1}`}
                    title="Gerar código a partir do nome"
                    disabled={disabled}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {showStock && draft.id && (
                <p className="text-xs text-muted-foreground">
                  Saldo: {formatStockQuantity(Number(draft.stock_quantity ?? 0))}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={addVariantDraft}
        disabled={!isOnline || disabled}
      >
        <Plus className="mr-2 h-4 w-4" />
        {value.length === 0 ? 'Adicionar variações' : 'Adicionar variação'}
      </Button>

      {!isOnline ? (
        <p className="flex items-center gap-1.5 text-xs text-amber-600">
          <WifiOff className="h-3.5 w-3.5 shrink-0" />
          Gerenciar variações exige conexão com a internet.
        </p>
      ) : (
        value.length > 0 && (
          <p className="text-xs text-muted-foreground">
            O produto passa a ser vendido pelas variações — é a variação que entra no orçamento e no
            estoque.
          </p>
        )
      )}
    </div>
  );
}
